import React, { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { adminAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Switch } from '../ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import RichTextEditor from '../ui/RichTextEditor';
import { 
  FileText, Plus, Edit2, Trash2, Loader2, 
  Users, Shield, UserCog, Building2, Check, Star,
  Send, Mail, MessageSquare, Bell, Calendar, AlertCircle,
  Clock, Search, RefreshCw
} from 'lucide-react';

const WAIVER_TYPES = [
  { id: 'customer', label: 'Customer', icon: Users, description: 'Default waiver for booking customers' },
  { id: 'admin', label: 'Admin', icon: Shield, description: 'Administrative staff agreements' },
  { id: 'tour_guide', label: 'Tour Guide', icon: UserCog, description: 'Tour guide liability agreements' },
  { id: 'service_provider', label: 'Service Provider', icon: Building2, description: 'Partner service provider contracts' },
];

// Default waiver reminder message with waiver signing link
const DEFAULT_WAIVER_REMINDER = `Hello {customer_name},

This is a friendly reminder that the following guests in your booking still need to complete their waivers before the tour:

{unsigned_guests}

Tour: {tour_name}
Date: {tour_date}

📝 Click the link below to sign your waiver now:
{waiver_link}

Please ensure all waivers are signed before the tour date.

Thank you!
Tour Finder Team`;

export default function WaiverTemplatesTab() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState('customer');
  const [showEditor, setShowEditor] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    is_default: false,
    is_active: true,
  });

  // Messaging state
  const [activeTab, setActiveTab] = useState('templates');
  const [bookings, setBookings] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [sendingReminder, setSendingReminder] = useState(false);
  const [reminderMethod, setReminderMethod] = useState('email');
  const [customMessage, setCustomMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Waiver reminder template
  const [waiverReminderTemplate, setWaiverReminderTemplate] = useState(DEFAULT_WAIVER_REMINDER);
  const [savingReminderTemplate, setSavingReminderTemplate] = useState(false);

  const loadTemplates = async () => {
    try {
      const response = await adminAPI.getWaiverTemplates();
      setTemplates(response.data || []);
    } catch (error) {
      console.error('Error loading waiver templates:', error);
      toast.error('Failed to load waiver templates');
    } finally {
      setLoading(false);
    }
  };

  const loadBookingsWithUnsignedWaivers = async () => {
    setLoadingBookings(true);
    try {
      const response = await adminAPI.getAllBookings();
      const allBookings = response.data || [];
      
      // Filter bookings with unsigned guests
      const bookingsWithUnsigned = allBookings.filter(booking => {
        if (booking.status === 'cancelled') return false;
        const guestInfo = booking.guest_info || [];
        return guestInfo.some(guest => !guest.waiver_signed);
      });
      
      setBookings(bookingsWithUnsigned);
    } catch (error) {
      console.error('Error loading bookings:', error);
      toast.error('Failed to load bookings');
    } finally {
      setLoadingBookings(false);
    }
  };

  useEffect(() => {
    loadTemplates();
  }, []);

  useEffect(() => {
    if (activeTab === 'reminders') {
      loadBookingsWithUnsignedWaivers();
    }
  }, [activeTab]);

  const getTemplatesByType = (type) => {
    return templates.filter(t => t.waiver_type === type);
  };

  const openCreateDialog = (type) => {
    setEditingTemplate(null);
    setFormData({
      title: '',
      content: '',
      is_default: false,
      is_active: true,
    });
    setSelectedType(type);
    setShowEditor(true);
  };

  const openEditDialog = (template) => {
    setEditingTemplate(template);
    setFormData({
      title: template.title,
      content: template.content,
      is_default: template.is_default,
      is_active: template.is_active,
    });
    setSelectedType(template.waiver_type);
    setShowEditor(true);
  };

  const handleSave = async () => {
    if (!formData.title.trim()) {
      toast.error('Please enter a title');
      return;
    }
    if (!formData.content.trim()) {
      toast.error('Please enter waiver content');
      return;
    }

    setSaving(true);
    try {
      if (editingTemplate) {
        await adminAPI.updateWaiverTemplate(editingTemplate.id, formData);
        toast.success('Waiver template updated');
      } else {
        await adminAPI.createWaiverTemplate({
          waiver_type: selectedType,
          ...formData,
        });
        toast.success('Waiver template created');
      }
      setShowEditor(false);
      loadTemplates();
    } catch (error) {
      console.error('Error saving waiver template:', error);
      toast.error(error.response?.data?.detail || 'Failed to save waiver template');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (template) => {
    if (!window.confirm(`Delete waiver template "${template.title}"?`)) return;
    
    try {
      await adminAPI.deleteWaiverTemplate(template.id);
      toast.success('Waiver template deleted');
      loadTemplates();
    } catch (error) {
      console.error('Error deleting waiver template:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete waiver template');
    }
  };

  const handleSetDefault = async (template) => {
    try {
      await adminAPI.updateWaiverTemplate(template.id, { is_default: true });
      toast.success(`Set "${template.title}" as default`);
      loadTemplates();
    } catch (error) {
      console.error('Error setting default:', error);
      toast.error('Failed to set as default');
    }
  };

  const handleToggleActive = async (template) => {
    try {
      await adminAPI.updateWaiverTemplate(template.id, { is_active: !template.is_active });
      loadTemplates();
    } catch (error) {
      console.error('Error toggling active:', error);
      toast.error('Failed to update template');
    }
  };

  // Send waiver reminder to main booking customer
  const sendWaiverReminder = async (booking) => {
    setSendingReminder(true);
    try {
      const response = await adminAPI.sendWaiverReminderAll(booking.id, {
        method: reminderMethod,
        custom_message: customMessage || null,
      });
      
      if (response.data?.success) {
        toast.success(`Waiver reminder sent via ${reminderMethod}`);
        setSelectedBooking(null);
        setCustomMessage('');
        loadBookingsWithUnsignedWaivers();
      } else {
        toast.error(response.data?.message || 'Failed to send reminder');
      }
    } catch (error) {
      console.error('Error sending waiver reminder:', error);
      toast.error(error.response?.data?.detail || 'Failed to send waiver reminder');
    } finally {
      setSendingReminder(false);
    }
  };

  // Format date helper
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Get unsigned guests for a booking
  const getUnsignedGuests = (booking) => {
    const guestInfo = booking.guest_info || [];
    return guestInfo.filter(guest => !guest.waiver_signed);
  };

  // Filter bookings by search
  const filteredBookings = bookings.filter(booking => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      booking.booker_name?.toLowerCase().includes(search) ||
      booking.booker_email?.toLowerCase().includes(search) ||
      booking.reference_number?.toLowerCase().includes(search) ||
      booking.tour_name?.toLowerCase().includes(search)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="waiver-templates-tab">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Waiver Management</h2>
          <p className="text-muted-foreground">Manage waiver templates and send reminders</p>
        </div>
      </div>

      {/* Main Tabs: Templates & Reminders */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid grid-cols-2 w-fit">
          <TabsTrigger value="templates" className="gap-2">
            <FileText className="h-4 w-4" />
            Templates
          </TabsTrigger>
          <TabsTrigger value="reminders" className="gap-2">
            <Bell className="h-4 w-4" />
            Send Reminders
            {bookings.length > 0 && (
              <Badge variant="destructive" className="ml-1">{bookings.length}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Templates Tab */}
        <TabsContent value="templates" className="mt-6">
          <Tabs defaultValue="customer" className="w-full">
            <TabsList className="grid grid-cols-4 gap-2 h-auto p-1">
              {WAIVER_TYPES.map((type) => {
                const Icon = type.icon;
                const typeTemplates = getTemplatesByType(type.id);
                return (
                  <TabsTrigger 
                    key={type.id} 
                    value={type.id}
                    className="flex items-center gap-2 py-3"
                  >
                    <Icon className="h-4 w-4" />
                    <span>{type.label}</span>
                    <Badge variant="secondary" className="ml-1">{typeTemplates.length}</Badge>
                  </TabsTrigger>
                );
              })}
            </TabsList>

            {WAIVER_TYPES.map((type) => {
              const Icon = type.icon;
              const typeTemplates = getTemplatesByType(type.id);
              
              return (
                <TabsContent key={type.id} value={type.id} className="mt-6">
                  <div className="mb-4 p-4 bg-blue-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon className="h-5 w-5 text-blue-600" />
                      <h3 className="font-semibold text-blue-900">{type.label} Waivers</h3>
                    </div>
                    <p className="text-sm text-blue-800">{type.description}</p>
                    {type.id === 'customer' && (
                      <p className="text-sm text-blue-700 mt-2">
                        <strong>Note:</strong> The default customer waiver is automatically used during the booking process.
                      </p>
                    )}
                  </div>

                  <div className="flex justify-end mb-4">
                    <Button onClick={() => openCreateDialog(type.id)} className="gap-2">
                      <Plus className="h-4 w-4" />
                      Create {type.label} Waiver
                    </Button>
                  </div>

                  {typeTemplates.length === 0 ? (
                    <Card className="p-8 text-center">
                      <FileText className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                      <p className="text-muted-foreground">No {type.label.toLowerCase()} waiver templates yet.</p>
                      <p className="text-sm text-muted-foreground">Create your first template to get started.</p>
                    </Card>
                  ) : (
                    <div className="space-y-4">
                      {typeTemplates.map((template) => (
                        <Card key={template.id} className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <h4 className="font-semibold text-lg">{template.title}</h4>
                                {template.is_default && (
                                  <Badge className="bg-green-100 text-green-800">
                                    <Star className="h-3 w-3 mr-1" /> Default
                                  </Badge>
                                )}
                                {!template.is_active && (
                                  <Badge variant="secondary">Inactive</Badge>
                                )}
                              </div>
                              <div 
                                className="text-sm text-muted-foreground line-clamp-2"
                                dangerouslySetInnerHTML={{ 
                                  __html: DOMPurify.sanitize(template.content.replace(/<[^>]*>/g, ' ').substring(0, 200) + '...') 
                                }}
                              />
                              <p className="text-xs text-muted-foreground mt-2">
                                Last updated: {template.updated_at ? new Date(template.updated_at).toLocaleDateString() : 'Never'}
                              </p>
                            </div>
                            <div className="flex items-center gap-2 ml-4">
                              <div className="flex items-center gap-2 mr-4">
                                <Label className="text-xs">Active</Label>
                                <Switch 
                                  checked={template.is_active}
                                  onCheckedChange={() => handleToggleActive(template)}
                                />
                              </div>
                              {!template.is_default && (
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => handleSetDefault(template)}
                                  className="gap-1"
                                >
                                  <Check className="h-3 w-3" />
                                  Set Default
                                </Button>
                              )}
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => openEditDialog(template)}
                              >
                                <Edit2 className="h-4 w-4" />
                              </Button>
                              {!template.is_default && (
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => handleDelete(template)}
                                  className="text-red-600 hover:text-red-700"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </div>
                        </Card>
                      ))}
                    </div>
                  )}
                </TabsContent>
              );
            })}
          </Tabs>
        </TabsContent>

        {/* Send Reminders Tab */}
        <TabsContent value="reminders" className="mt-6">
          <div className="space-y-6">
            {/* Header with refresh and search */}
            <div className="flex flex-col sm:flex-row justify-between gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by name, email, or reference..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              <Button 
                variant="outline" 
                onClick={loadBookingsWithUnsignedWaivers}
                disabled={loadingBookings}
                className="gap-2"
              >
                <RefreshCw className={`h-4 w-4 ${loadingBookings ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>

            {/* Info Box */}
            <Card className="p-4 bg-amber-50 border-amber-200">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-amber-900">Waiver Reminder Center</h4>
                  <p className="text-sm text-amber-800 mt-1">
                    Send waiver reminders to the main booking customer for bookings with unsigned guests. 
                    Reminders can be sent via Email or SMS.
                  </p>
                </div>
              </div>
            </Card>

            {/* Bookings List */}
            {loadingBookings ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : filteredBookings.length === 0 ? (
              <Card className="p-8 text-center">
                <Check className="h-12 w-12 mx-auto mb-4 text-green-500" />
                <h3 className="font-semibold text-lg text-green-800">All Waivers Signed!</h3>
                <p className="text-muted-foreground mt-1">
                  {searchTerm 
                    ? 'No bookings match your search criteria.'
                    : 'There are no bookings with unsigned waivers.'}
                </p>
              </Card>
            ) : (
              <div className="space-y-4">
                {filteredBookings.map((booking) => {
                  const unsignedGuests = getUnsignedGuests(booking);
                  const tourDate = new Date(booking.tour_date || booking.date);
                  const isUpcoming = tourDate > new Date();
                  const daysUntil = Math.ceil((tourDate - new Date()) / (1000 * 60 * 60 * 24));
                  
                  return (
                    <Card 
                      key={booking.id} 
                      className={`p-4 ${daysUntil <= 3 && isUpcoming ? 'border-red-300 bg-red-50/50' : ''}`}
                    >
                      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h4 className="font-semibold">{booking.booker_name}</h4>
                            <Badge variant="outline" className="text-xs">
                              {booking.reference_number}
                            </Badge>
                            {daysUntil <= 3 && isUpcoming && (
                              <Badge variant="destructive" className="text-xs">
                                {daysUntil === 0 ? 'Today!' : `${daysUntil} day${daysUntil > 1 ? 's' : ''} away`}
                              </Badge>
                            )}
                          </div>
                          
                          <div className="text-sm text-muted-foreground space-y-1">
                            <p className="flex items-center gap-2">
                              <Mail className="h-3 w-3" />
                              {booking.booker_email}
                            </p>
                            <p className="flex items-center gap-2">
                              <Calendar className="h-3 w-3" />
                              {booking.tour_name} - {formatDate(booking.tour_date || booking.date)}
                            </p>
                          </div>
                          
                          {/* Unsigned Guests */}
                          <div className="mt-3">
                            <p className="text-xs font-medium text-red-600 mb-1">
                              Unsigned Waivers ({unsignedGuests.length}):
                            </p>
                            <div className="flex flex-wrap gap-2">
                              {unsignedGuests.map((guest, idx) => (
                                <Badge key={idx} variant="secondary" className="text-xs bg-red-100 text-red-700">
                                  {guest.full_name || `Guest ${idx + 1}`}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex flex-col sm:flex-row gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setSelectedBooking(booking);
                              setReminderMethod('email');
                            }}
                            className="gap-2"
                          >
                            <Mail className="h-4 w-4" />
                            Email Reminder
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setSelectedBooking(booking);
                              setReminderMethod('sms');
                            }}
                            className="gap-2"
                          >
                            <MessageSquare className="h-4 w-4" />
                            SMS Reminder
                          </Button>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Edit/Create Dialog */}
      <Dialog open={showEditor} onOpenChange={setShowEditor}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingTemplate ? 'Edit Waiver Template' : `Create ${WAIVER_TYPES.find(t => t.id === selectedType)?.label} Waiver`}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="title">Template Title</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., Standard Liability Waiver"
              />
            </div>
            
            <div>
              <Label>Waiver Content</Label>
              <p className="text-xs text-muted-foreground mb-2">
                Use the editor below to format your waiver. Include all necessary legal terms.
              </p>
              <div className="border rounded-lg">
                <RichTextEditor
                  value={formData.content}
                  onChange={(content) => setFormData({ ...formData, content })}
                  placeholder="Enter your waiver content here..."
                />
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Switch
                  id="is_default"
                  checked={formData.is_default}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_default: checked })}
                />
                <Label htmlFor="is_default">Set as default for {selectedType} waivers</Label>
              </div>
              
              <div className="flex items-center gap-2">
                <Switch
                  id="is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                />
                <Label htmlFor="is_active">Active</Label>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditor(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {editingTemplate ? 'Update Template' : 'Create Template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Reminder Dialog */}
      <Dialog open={!!selectedBooking} onOpenChange={(open) => !open && setSelectedBooking(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {reminderMethod === 'email' ? <Mail className="h-5 w-5" /> : <MessageSquare className="h-5 w-5" />}
              Send Waiver Reminder via {reminderMethod === 'email' ? 'Email' : 'SMS'}
            </DialogTitle>
          </DialogHeader>
          
          {selectedBooking && (
            <div className="space-y-4 py-4">
              <Card className="p-4 bg-muted/50">
                <div className="space-y-2 text-sm">
                  <p><strong>Customer:</strong> {selectedBooking.booker_name}</p>
                  <p><strong>{reminderMethod === 'email' ? 'Email' : 'Phone'}:</strong> {
                    reminderMethod === 'email' 
                      ? selectedBooking.booker_email 
                      : selectedBooking.booker_phone || 'No phone number'
                  }</p>
                  <p><strong>Tour:</strong> {selectedBooking.tour_name}</p>
                  <p><strong>Date:</strong> {formatDate(selectedBooking.tour_date || selectedBooking.date)}</p>
                  
                  <div className="pt-2 border-t mt-2">
                    <p className="text-red-600 font-medium">Unsigned Guests:</p>
                    <ul className="list-disc list-inside text-muted-foreground">
                      {getUnsignedGuests(selectedBooking).map((guest, idx) => (
                        <li key={idx}>{guest.full_name || `Guest ${idx + 1}`}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </Card>

              {/* Method Selection */}
              <div>
                <Label>Send Via</Label>
                <div className="flex gap-2 mt-2">
                  <Button
                    type="button"
                    variant={reminderMethod === 'email' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setReminderMethod('email')}
                    className="gap-2"
                  >
                    <Mail className="h-4 w-4" />
                    Email
                  </Button>
                  <Button
                    type="button"
                    variant={reminderMethod === 'sms' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setReminderMethod('sms')}
                    className="gap-2"
                    disabled={!selectedBooking.booker_phone}
                  >
                    <MessageSquare className="h-4 w-4" />
                    SMS
                  </Button>
                </div>
                {!selectedBooking.booker_phone && reminderMethod === 'sms' && (
                  <p className="text-xs text-red-500 mt-1">No phone number available for this booking</p>
                )}
              </div>

              {/* Custom Message (Optional) */}
              <div>
                <Label>Custom Message (Optional)</Label>
                <Textarea
                  value={customMessage}
                  onChange={(e) => setCustomMessage(e.target.value)}
                  placeholder="Add a personal note to include with the reminder..."
                  rows={3}
                  className="mt-2"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Leave blank to use the default waiver reminder template
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedBooking(null)}>
              Cancel
            </Button>
            <Button 
              onClick={() => sendWaiverReminder(selectedBooking)} 
              disabled={sendingReminder || (reminderMethod === 'sms' && !selectedBooking?.booker_phone)}
              className="gap-2"
            >
              {sendingReminder ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              Send Reminder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
